import React, { useEffect, useRef, useState } from "react";
import { requestFastThenImprove, getLastLocation } from "../utils/location_manager";

const LocationSwitch = ({ onImprove }) => {
  const [enabled, setEnabled] = useState(false);
  const watcherStopRef = useRef(null);
  const togglingRef = useRef(false); // guard against rapid toggles

  // Load persisted opt-in
  useEffect(() => {
    try {
      const stored = localStorage.getItem("location_opt_in");
      setEnabled(stored === "true");
    } catch {
      setEnabled(false);
    }
  }, []);

  // If enabled on mount/toggle, start location flow
  useEffect(() => {
    // stop any prior watcher if disabling
    if (!enabled) {
      if (watcherStopRef.current) {
        watcherStopRef.current();
        watcherStopRef.current = null;
      }
      return;
    }

    // Serve the freshest cached fix immediately (optimistic)
    try {
      const cached = getLastLocation();
      if (cached && onImprove) onImprove(cached);
    } catch {}

    // Kick an immediate fast fetch + short improvement window
    const { stop } = requestFastThenImprove({
      windowMs: 9000,
      onUpdate: (loc) => {
        if (!loc) return;
        try {
          localStorage.setItem("last_location", JSON.stringify(loc));
        } catch {}
        if (onImprove) onImprove(loc);
      },
      onEvent: (evt) => {
        // Optional: swap to your logger if desired
        // console.debug("[LocationSwitch]", evt);
      }
    });

    watcherStopRef.current = stop;

    // Auto-stop when page/tab goes background (saves battery)
    const onVisibility = () => {
      if (document.hidden && watcherStopRef.current) {
        watcherStopRef.current();
        watcherStopRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility, { passive: true });

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (watcherStopRef.current) {
        watcherStopRef.current();
        watcherStopRef.current = null;
      }
    };
  }, [enabled, onImprove]);

  const handleToggle = () => {
    if (togglingRef.current) return;
    togglingRef.current = true;
    setTimeout(() => (togglingRef.current = false), 250); // small debounce

    const next = !enabled;
    setEnabled(next);
    try {
      localStorage.setItem("location_opt_in", next.toString());
    } catch {}

    if (next) {
      // Prime a very quick fix so SmartAlert has something cached ASAP
      const { stop } = requestFastThenImprove({
        windowMs: 3000, // quick prime; main effect above will run 9s cycle
        onUpdate: (loc) => {
          if (!loc) return;
          try {
            localStorage.setItem("last_location", JSON.stringify(loc));
          } catch {}
        }
      });
      // stop the primer after window elapses automatically; no ref kept
    } else {
      // turning off clears watcher via effect; keep last_location for fallback
    }
  };

  const colors = {
    on: { track: "#0ea5e9", label: "#0f172a" },   // sky blue when ON
    off: { track: "#94a3b8", label: "#475569" },  // slate when OFF
  };
  const c = enabled ? colors.on : colors.off;

  return (
    <label
      style={styles.container}
      onClick={handleToggle}
      role="switch"
      aria-checked={enabled}
      title={enabled ? "Location: On" : "Location: Off"}
    >
      <span style={{ ...styles.label, color: c.label }}>
        {enabled ? "Location: On" : "Location: Off"}
      </span>
      <div style={{ ...styles.switch, backgroundColor: c.track }}>
        <div style={{ ...styles.ball, left: enabled ? 21 : 1 }} />
      </div>
    </label>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    userSelect: "none",
    margin: 0,
    padding: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    margin: 0,
    padding: 0,
  },
  switch: {
    width: 42,
    height: 22,
    borderRadius: 22,
    position: "relative",
    transition: "background-color 0.25s ease",
    boxSizing: "border-box",
  },
  ball: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    backgroundColor: "#fff",
    position: "absolute",
    top: 2,
    transition: "left 0.25s ease-in-out",
    left: 1,
    boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

export default LocationSwitch;
